import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { IoCall } from "react-icons/io5";
import { MdRequestQuote } from "react-icons/md";
import { FaRupeeSign, FaRegAddressCard, FaMapMarkedAlt } from "react-icons/fa";
import { BsClockHistory } from "react-icons/bs";
import { RiHomeHeartLine } from "react-icons/ri";
import { reactLocalStorage } from 'reactjs-localstorage';

import withRouter from './../../util/WithRouter';
import Const from '../../util/Constants';
import Util from '../../util/Util';

import LeadAPI from '../../api/LeadAPI';
import CallAPI from '../../api/CallAPI';
import MeetingAPI from '../../api/MeetingAPI';
import SalesOrderAPI from '../../api/SalesOrderAPI';
import PropertyAPI from '../../api/PropertyAPI';
import DailySalesReportAPI from '../../api/DailySalesReportAPI';

import StatCard from './mydashboardcomponent/StatCard';
import MainBarChart from './mydashboardcomponent/MainBarChart';
import PieChartComponent from './mydashboardcomponent/PieChartComponent';
import MiniCalendar from './mydashboardcomponent/MiniCalendar';
import HourlyCallChart from './mydashboardcomponent/HourlyCallChart';
import LatestSiteVisits from './mydashboardcomponent/LatestSiteVisits';
import Display from '../../util/Display';

class MyDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.models = reactLocalStorage.getObject(Const.Model);
    this.meetingModel = this.models.meeting;
    this.user = reactLocalStorage.getObject(Const.LoggedInUser);
    this.leadStatusOptions = this.models.lead.fields['progress'].options;
    this.leadSourceOptions = this.models.source.options;
    this.updatedLeadStatusOptions = [];
    this.updatedLeadSourceOptions = [];
    this.state = {
      leadCount: 0,
      callCount: 0,
      callStats: { completed: 0, pending: 0 },
      callTalkTime: "0h 0m",
      meetingCount: 0,
      meetingStats: { completed: 0, pending: 0 },
      salesCount: 0,
      salesAmount: 0,
      propertyCount: 0,
      propertyStats: { available: 0, booked: 0 },
      hourlyCallData: [],
      siteVisitData: [],
      leadSourceData: [],
    };
  }

  componentDidMount() {
    this.fetchAllStats();
  }

  componentDidUpdate(prevProps) {
    const propKeys = ['responsibleId', 'startDate', 'endDate', 'refreshKey'];
    const hasChanged = propKeys.some(key => this.props[key] !== prevProps[key]);
    if (hasChanged) {
      this.fetchAllStats();
    }
  }

  getResponsibleFilter = () => {
    const id = this.props.responsibleId;
    return id ? `~${id}~` : '';
  };

  fetchAllStats = () => {
    this.fetchLeadCount();
    this.fetchCallCount();
    this.fetchMeetingCount();
    this.fetchSalesOrder();
    this.fetchPropertyCount();
    this.fetchLeadStatus();
    this.fetchLeadSource();
    this.fetchLatestMeetings();
  };

  fetchLeadCount = () => {
    const filters = [
      {
        field: "responsible_person",
        condition: "in",
        value: this.getResponsibleFilter()
      }
    ];

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatWithTime = (date, time) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${time}`;
    };

    const startDate = this.props.startDate || formatWithTime(startOfMonth, "18:30:00");
    const endDate = this.props.endDate || formatWithTime(endOfMonth, "18:29:59");

    filters.push({
      field: "enquiry_date",
      condition: "in",
      value: `${startDate}~${endDate}`
    });

    const list = {
      filters,
      pagination: { currentPage: 1, perPage: 1 }
    };

    LeadAPI.count(
      list,
      (response) => this.setState({ leadCount: response?.count || 0 }),
      (error) => console.error("Failed to fetch lead count:", error)
    );
  };

  fetchCallCount = () => {
    const filters = [
      {
        field: "responsible_person",
        condition: "in",
        value: this.getResponsibleFilter()
      }
    ];

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const startDate = `${yesterday.toISOString().slice(0, 10)} 18:30:00`;
    const endDate = `${today.toISOString().slice(0, 10)} 18:29:59`;

    filters.push({
      field: "date_time",
      condition: "in",
      value: `${startDate}~${endDate}`
    });

    const list = {
      filters,
      pagination: { currentPage: 1, perPage: 10000 }
    };

    CallAPI.getList(
      list,
      (response) => {
        const allCalls = response?.data || [];

        const completed = allCalls.filter(call => call.complete === 1).length;
        const pending = allCalls.filter(call => call.complete === 0).length;
        const totalDurationSec = allCalls
          .filter(call => call.duration)
          .reduce((sum, call) => sum + call.duration, 0);

        const formatDuration = (seconds) => {
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          return `${hours}h ${minutes}m`;
        };

        const hourlyMap = {};
        for (let i = 9; i <= 21; i++) hourlyMap[i] = 0;

        allCalls.forEach(call => {
          if (call.complete === 1 && call.date_time) {
            const dateObj = Util.serverStringDateTimeToDate(call.date_time);
            if (dateObj) {
              const localHour = dateObj.getHours();
              if (hourlyMap[localHour] !== undefined) {
                hourlyMap[localHour]++;
              }
            }
          }
        });

        const hourlyData = Object.keys(hourlyMap).map(hour => ({
          hour: `${(hour % 12 || 12)} ${hour < 12 ? 'AM' : 'PM'}`,
          count: hourlyMap[hour]
        }));

        this.setState({
          callCount: allCalls.length,
          callStats: { completed, pending },
          callTalkTime: formatDuration(totalDurationSec),
          hourlyCallData: hourlyData
        });
      },
      (error) => console.error("Failed to fetch call list:", error)
    );
  };

  fetchMeetingCount = () => {
    const filters = [
      {
        field: "responsible_person",
        condition: "in",
        value: this.getResponsibleFilter()
      }
    ];

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const startDate = `${yesterday.toISOString().slice(0, 10)} 18:30:00`;
    const endDate = `${today.toISOString().slice(0, 10)} 18:29:59`;

    filters.push({
      field: "date_time",
      condition: "in",
      value: `${startDate}~${endDate}`
    });

    const list = {
      filters,
      pagination: { currentPage: 1, perPage: 10000 }
    };

    MeetingAPI.getList(
      list,
      (response) => {
        const allMeetings = response?.data || [];
        const completed = allMeetings.filter(m => m.complete === 1).length;
        const pending = allMeetings.filter(m => m.complete === 0).length;

        this.setState({
          meetingCount: allMeetings.length,
          meetingStats: { completed, pending }
        });
      },
      (error) => console.error("Failed to fetch meeting list:", error)
    );
  };

  fetchLatestMeetings = () => {
    const filters = [
      {
        field: "responsible_person",
        condition: "in",
        value: this.getResponsibleFilter()
      }
    ];

    const list = {
      filters,
      pagination: { currentPage: 1, perPage: 10000 }
    };

    MeetingAPI.getList(
      list,
      (response) => {
        const allMeetings = response?.data || [];
        const recentVisits = allMeetings
          .filter(m => m.complete === 1)
          .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
          .slice(0, 7)
          .map(meeting => ({
            id: meeting.id,
            leadName: meeting.lead_first || "N/A",
            phone: (meeting.lead_phone || '').replace(/~/g, '') || "N/A",
            datetime: meeting.date_time,
            displayDate: Util.serverStringToDisplayDate(meeting.date_time),
            displayTime: Util.serverStringToDisplayTime(meeting.date_time),
            feedback: Display.FieldValue(meeting, 'feedback', this.meetingModel.fields),
            comment: meeting.comment || "",
            modelId: meeting.model_id,
            modelNo: meeting.model_no,

          }));

        this.setState({ siteVisitData: recentVisits });
      },
      (error) => console.error("Failed to fetch recent meetings:", error)
    );
  };


  fetchSalesOrder = () => {
    const filters = [
      { field: "responsible_person", condition: "in", value: this.getResponsibleFilter() }
    ];

    const list = {
      filters,
      pagination: { currentPage: 1, perPage: 10000 }
    };

    SalesOrderAPI.getList(
      list,
      (response) => {
        const allSales = response?.data || [];
        const totalAmount = allSales.reduce((sum, s) => sum + parseFloat(s.total || 0), 0);
        this.setState({ salesCount: allSales.length, salesAmount: totalAmount });
      },
      (error) => console.error("Failed to fetch sales orders:", error)
    );
  };

  fetchPropertyCount = () => {
    const list = {
      filters: [],
      pagination: { currentPage: 1, perPage: 10000 }
    };

    PropertyAPI.list(
      list,
      (response) => {
        const properties = response?.data || [];
        const available = properties.filter(p => p.property_status === 1).length;
        const booked = properties.length - available;

        this.setState({
          propertyCount: response.count,
          propertyStats: { available, booked }
        });
      },
      (error) => console.error("Failed to fetch properties:", error)
    );
  };

  fetchLeadStatus = () => {
    let { startDate, endDate } = this.props;

    if (!startDate || !endDate) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const formatWithTime = (date, time) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${time}`;
      };

      startDate = formatWithTime(startOfMonth, "18:30:00");
      endDate = formatWithTime(endOfMonth, "18:29:59");
    }

    const filters = [
      { field: "responsible_person", condition: "in", value: this.getResponsibleFilter() },
      { field: "enquiry_date", condition: "in", value: `${startDate}~${endDate}` }
    ];

    const list = {
      filters,
      model: "lead_model",
      x: "progress",
      y: "",
      z: "COUNT(id)"
    };

    DailySalesReportAPI.getFixedReport(
      list,
      (response) => {
        const updated = this.leadStatusOptions.map(opt => {
          const match = response.find(r => r.progress === opt.value);
          return { name: opt.label, value: opt.value, count: match ? match.sum : 0 };
        });
        this.updatedLeadStatusOptions = updated;
      },
      (error) => console.error("Failed to fetch lead status:", error)
    );
  };

  fetchLeadSource = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatWithTime = (date, time) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${time}`;
    };

    const startDate = this.props.startDate || formatWithTime(startOfMonth, "18:30:00");
    const endDate = this.props.endDate || formatWithTime(endOfMonth, "18:29:59");

    const filters = [
      { field: "responsible_person", condition: "in", value: this.getResponsibleFilter() },
      { field: "enquiry_date", condition: "in", value: `${startDate}~${endDate}` }
    ];

    const list = {
      filters,
      model: "lead_model",
      x: "source",
      y: "",
      z: "COUNT(id)"
    };

    DailySalesReportAPI.getFixedReport(
      list,
      (response) => {
        const updated = this.leadSourceOptions.map(opt => {
          const match = response.find(r => r.source === opt.value);
          return { name: opt.label, value: match ? match.sum : 0 };
        });

        this.setState({ leadSourceData: updated });
      },
      (error) => console.error("Failed to fetch lead source:", error)
    );
  };


  render() {
    const {
      leadCount, callCount, callStats, callTalkTime,
      meetingCount, meetingStats, salesCount, salesAmount,
      propertyCount, propertyStats, hourlyCallData, siteVisitData
    } = this.state;

    const topStats = [
      { icon: FaRegAddressCard, title: (<>My Leads <em>(This Month)</em> </>), count: leadCount, color: "#007bff" },
      {
        icon: IoCall, title: (<>My Calls <em>(today)</em> </>), count: callCount, color: "#28a745",
        subtext: (
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', fontWeight: 500 }}>
            <span style={{ color: '#28a745' }}>Completed: {callStats.completed}</span>
            <span style={{ color: '#FF0000' }}>Pending: {callStats.pending}</span>
          </div>
        )
      },
      { icon: BsClockHistory, title: (<>Call Talktime <em>(today)</em> </>), count: callTalkTime, color: "#20c997" },
      {
        icon: FaMapMarkedAlt, title: (<>My Site Visits <em>(today)</em> </>), count: meetingCount, color: "#ffc107",
        subtext: (
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', fontWeight: 500 }}>
            <span style={{ color: '#28a745' }}>Completed: {meetingStats.completed}</span>
            <span style={{ color: '#FF0000' }}>Pending: {meetingStats.pending}</span>
          </div>
        )
      },
      {
        icon: RiHomeHeartLine, title: (<>Properties <em>(All)</em> </>), count: propertyCount, color: "#17a2b8",

      },
      {
        icon: FaRupeeSign, title: (<>My Bookings <em>(All)</em> </>), count: salesCount, color: "#6f42c1",
        subtext: (
          <div style={{ fontSize: '0.875rem', marginTop: '6px', color: '#495057' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
              <FaRupeeSign style={{ fontSize: '0.85rem', color: '#6f42c1' }} />
              INR: {salesAmount?.toLocaleString('en-IN')}
            </span>
          </div>
        )
      }
    ];

    return (
      <Container fluid style={{ backgroundColor: "#ffffff" }}>
        <Row className="mb-3 mt-2 g-2">
          {topStats.map((stat, index) => (
            <Col key={index} xl={2} lg={4} md={6}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>
        <Row className="mb-3 g-3">
          <Col xl={8} lg={12}>
            <Row className="g-3">
              <Col md={6}>
                <MainBarChart data={this.updatedLeadStatusOptions} title="Lead Status" />
              </Col>
              <Col md={6}>
                <PieChartComponent data={this.state.leadSourceData} title="Lead Sources" />
              </Col>
            </Row>
          </Col>
          <Col xl={4} lg={6}>
            <MiniCalendar filters={{ field: "responsible_person", condition: "in", value: this.getResponsibleFilter() }} navigate={this.props.navigate} />
          </Col>
        </Row>
        <Row className="mb-3 g-3">
          <Col xl={8} lg={12}>
            <Row className="g-3">
              <Col md={6}>
                <HourlyCallChart data={hourlyCallData} />
              </Col>
              <Col md={6}>
                <LatestSiteVisits data={siteVisitData} navigate={this.props.navigate} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default withRouter(MyDashboard);
